import { Injectable } from '@angular/core';
import { DoorActiveConfiguration, DoorSlat } from '@icc/window-door/types';
import { SlatsComponent } from './slats.component';
import { ActiveSash } from '@icc/window-door/types';
import { ListPageComponent } from '@icc/configurator/pages';
import { InfoService, _, isDefined, isObject, logger } from '@icc/shared/helpers';
import { ModalService } from '@icc/configurator/shared';
import { SizesService } from '../sizes/sizes.service';
import { EventBusService, ConfiguratorsDataService, TranslateService } from '@icc/shared';
import { SizeRangeService } from '@icc/configurator/shared';
import { DoorSizes } from '@icc/window-door/types';
import { SystemsService } from '../../modules/systems/systems.service';
import { NewColorsService } from '@icc/construction-elements/configurator';
import { FillingsFacade } from '../../glazings/fillings.facade';

@Injectable({
  providedIn: 'root'
})
export class SlatsService {

    slats: DoorSlat[] | undefined;
    doorSizes: DoorSizes;

    constructor(
        private modalService: ModalService,
        private configuratorsDataService: ConfiguratorsDataService,
        private sizeRangeService: SizeRangeService,
        private sizesService: SizesService,
        private eventBusService: EventBusService,
        private newColorService: NewColorsService,
        private infoFactory: InfoService,
        private translateService: TranslateService,
        public fillingsFacade: FillingsFacade,
    ) {
        this.eventBusService.subscribeWithoutConfiguration('initializedConfigurator', () => {
            this.init();
        });

        this.eventBusService.subscribe(['changedSashes', 'changedFillings'], data => {
                try {
                    this.validateAndFixIssues(data.activeConfiguration as DoorActiveConfiguration);
                } catch (err) {
                    logger.error(err);
                }
            });
    }

    private init() {
        this.slats = this.configuratorsDataService.data.slats;
    }

    openModal(conf: DoorActiveConfiguration, sash: ActiveSash) {
        const filling = sash.glazing;
        const doorSizeId = conf.doorSizes.sashSizeId;
        const slatsId = filling.slats?.find(slat => slat.doorSizeId === doorSizeId)?.slatsId
        const modalInstance = this.modalService.open({
            pageComponent: ListPageComponent,
            resolve: {
                title: () => _('DOOR|Lamele'),
                selectedId: () => conf?.Slat?.id || null,
                items: () => this.slats.filter(slat => slatsId.includes(slat.id)),
                imageUrl: () => '/files/slat',
            },
        });

        modalInstance.result.then(selectedSlat => {
            if (isObject(selectedSlat) && isDefined(selectedSlat)) {
                const slatSpace = this.getSlatSpace(conf, sash, selectedSlat);
                this.modalService.open({
                    pageComponent: SlatsComponent,
                    resolve: {
                        slat: () => selectedSlat,
                        slatSpace: () => slatSpace
                    },
               }).result.then(selectedData => {
                    this.selectSlat(conf, selectedSlat, sash, selectedData.slatCount)
               });
            }
        });
    }

    selectSlat(conf: DoorActiveConfiguration, slat: DoorSlat, sash: ActiveSash, slatCount: number) {
        conf.Slat = {...slat, count: slatCount};
        this.newColorService.setDefaultColorFor(conf, {element: 'slat'})
        this.eventBusService.post({key: 'icc-redraw', value: {}});
        this.eventBusService.post({key: 'changeSlat', value: {}});
        this.eventBusService.post({ key: 'processDependencies', value: null });
    }

    removeSlat(conf: DoorActiveConfiguration) {
        conf.Slat = null;
        this.eventBusService.post({key: 'icc-redraw', value: {}});
        this.eventBusService.post({key: 'changeSlat', value: {}});
        this.eventBusService.post({ key: 'processDependencies', value: null });
    }

    slatAvailable(conf: DoorActiveConfiguration) {
        const activeSash = this.fillingsFacade.getDoorSash(conf, 'doorActive');
        const doorSizeId = conf.doorSizes.sashSizeId;
        const slats = activeSash?.glazing?.slats;
        if(!slats || slats.length < 0) {
            return false;
        }
        const blockadeSlats = conf.Dependencies?.filter(p => p.type === 'blockadeAccessory');
        const dependencies = this.configuratorsDataService.data.dependencies || [];
        const blockSlats = [];
        dependencies.forEach(dependency => {
            blockadeSlats.forEach(blocked => {
                if (dependency && blocked && Number(dependency.id) === Number(String(blocked.id).split('-')[0])) {
                    if(blocked.type === 'blockadeAccessory') {
                        const dependencyAction = dependency.actions.find((p: any) => p.type === 'blockadeAccessory');
                        const dependencyValue = dependencyAction?.value ?? [];
                        const slatsId = (Array.isArray(dependencyValue)
                            ? dependencyValue
                            : [dependencyValue]
                        )
                            .map(id => id == null ? '' : String(id))
                            .filter(id => id.startsWith('slat_'))
                            .map(id => Number(id.split('_')[1]))
                            .filter(Number.isFinite);
                        const hideAccessory = dependencyAction?.valueType === 'hide' || false;
                        if(hideAccessory) {
                            blockSlats.push(...slatsId);
                        }
                    }
                }
            });
        });

        return this.slats.some(slat => this.getSlatSpace(conf, activeSash, slat).maxSlatCount > 0)
            && slats.filter(slat => !slat.slatsId.every(id => blockSlats.map(Number).includes(id)))
                .some(slat => slat.doorSizeId === doorSizeId);
    }

    private validateAndFixIssues(conf: DoorActiveConfiguration) {
        if(!conf.Slat) {
            return;
        }
        const activeSash = this.fillingsFacade.getDoorSash(conf, 'doorActive');
        const filling = activeSash.glazing;
        const doorSizeId = conf.doorSizes.sashSizeId;
        const slatsId = filling.slats?.find(slat => slat.doorSizeId === doorSizeId)?.slatsId || []
        if(!slatsId.includes(conf.Slat.id)) {
            this.infoFactory.openWarning(this.translateService.instant('DOOR|Obecna lamela nie może zostać zastosowana. Lamela usunięte z konfiguracji.'));
            this.removeSlat(conf);
            return;
        }
        const slatSpace = this.getSlatSpace(conf, activeSash, conf.Slat);
        if(slatSpace.maxSlatCount < 1) {
            this.infoFactory.openWarning(this.translateService.instant('DOOR|Obecna lamela nie może zostać zastosowana. Lamela usunięte z konfiguracji.'));
            this.removeSlat(conf);
        } else if(slatSpace.maxSlatCount < conf.Slat.count) {
            this.infoFactory.openWarning(this.translateService.instant('DOOR|Ze względu na ograniczenia wymiarowe zmniejszono liczbe lameli.'));
            conf.Slat.count = slatSpace.maxSlatCount;
            this.eventBusService.post({key: 'icc-redraw', value: {}});
        }
    }

    private getSlatSpace(conf: DoorActiveConfiguration, sash: ActiveSash, slat: DoorSlat) {
        const doorSize = conf.doorSizes;
        const fillingSlat = sash.glazing.slats?.find(s => s.doorSizeId === doorSize.sashSizeId && s.slatsId.includes(slat.id));
        if(!fillingSlat) {
            return {
                sashSlatSpace: 0,
                maxSlatCount: 0
            }
        }
        let sashSlatSpace = fillingSlat.space;
        if(fillingSlat.ratio) {
            const steps = {
                height: conf.doorSizes?.sizeIntervalHeight || 1,
                width: conf.doorSizes?.sizeIntervalCustomWidth || 1,
            }
            const rangeId = this.sizesService.getSizeRangeId(conf.doorSizes);
            const passiveSash = conf.Sashes.find(s => s.type.passive);
            const width = conf?.Width - (conf?.doorSizes?.leftLightSize || 0) - (conf?.doorSizes?.rightLightSize || 0) - (passiveSash?.rWidth || 0);
            const sizeRanges = this.sizeRangeService.getSizeRange(rangeId, width, conf?.Height, true, steps);
            const ratio = (width - sizeRanges.width.minX) * fillingSlat.ratio;
            sashSlatSpace += ratio;
        }
        if(sash.type.out_open) {
            sashSlatSpace += conf.UsedProfiles.find(p => p.type === 'virtual_door_sash').rebateWidthOut;
            sashSlatSpace -= slat.outerHingeOffset;
        } else {
            sashSlatSpace -= slat.innerHingeOffset;
        }
        const slatSet = slat.width + slat.space;
        let maxSlatCount = Math.floor(sashSlatSpace / slatSet);
        const slatsSpace = maxSlatCount * slatSet
        if(sashSlatSpace - slatsSpace >= slat.width) {
            maxSlatCount++;
        }
        return {
            sashSlatSpace,
            maxSlatCount
        }
    }
}


